import React from 'react';
import PropTypes from 'prop-types';
import { ChevronRight } from 'lucide-react';
import AgentAvatar from './AgentAvatar';
import { formatPeriod } from '../helpers/callReviewsDates';
import { cn } from 'lib/utils';

const headerClassName =
  'bg-grey-lightest px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-grey-light';
const cellClassName = 'px-5 py-3.5';

/**
 * the agency roster, where every row expands in place to that agent's reports.
 * only one row is open at a time, so the question of which table is on screen is
 * never ambiguous.
 * @param items - AnalysisAgentSummary rows for the current page
 * @param expandedAgentId - agent id of the open row, or null
 * @param onToggleAgent - called with an agent id to open or close its row
 * @param renderExpanded - renders the body of the open row
 */
export default function AgentsRosterTable({
  items,
  expandedAgentId,
  onToggleAgent,
  renderExpanded
}) {
  if (items.length === 0) {
    return (
      <p className='px-5 py-10 text-center text-[length:--font-size-body] text-grey'>
        No reports yet.
      </p>
    );
  }

  return (
    <table className='w-full border-collapse'>
      <thead>
        <tr className='border-y border-grey-lighter'>
          <th className={headerClassName}>Agent</th>
          <th className={headerClassName}>NPN</th>
          <th className={headerClassName}>Reports</th>
          <th className={headerClassName}>Latest date range</th>
          <th className={cn(headerClassName, 'w-px text-right')}>
            <span className='sr-only'>Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map(agent => {
          const isExpanded = expandedAgentId === agent.agent_id;
          const panelId = `agent-files-${agent.agent_id}`;

          return (
            <React.Fragment key={agent.agent_id}>
              <tr
                className={cn(
                  'cursor-pointer border-b border-grey-lighter transition-colors',
                  isExpanded ? 'bg-grey-lightest' : 'hover:bg-grey-lightest/60'
                )}
                onClick={() => onToggleAgent(agent.agent_id)}
              >
                <td className={cellClassName}>
                  <span className='flex items-center gap-3'>
                    <AgentAvatar
                      firstName={agent.first_name}
                      lastName={agent.last_name}
                    />
                    <span className='font-semibold text-black'>
                      {agent.first_name} {agent.last_name}
                    </span>
                  </span>
                </td>
                {/* npn is a bigint string: rendering it as-is keeps leading zeros */}
                <td className={cn(cellClassName, 'text-grey')}>{agent.npn}</td>
                <td className={cn(cellClassName, 'text-grey')}>
                  {agent.files_count} PDFs
                </td>
                <td className={cn(cellClassName, 'text-grey')}>
                  {formatPeriod(agent.latest_from_date, agent.latest_to_date)}
                </td>
                <td className={cn(cellClassName, 'text-right')}>
                  <button
                    type='button'
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    className='inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 font-medium text-primary-darkened'
                    onClick={event => {
                      event.stopPropagation();
                      onToggleAgent(agent.agent_id);
                    }}
                  >
                    {isExpanded ? 'Hide' : 'View'}
                    <ChevronRight
                      className={cn(
                        'size-4 transition-transform duration-200',
                        isExpanded && 'rotate-90'
                      )}
                      aria-hidden
                    />
                  </button>
                </td>
              </tr>
              {isExpanded && (
                <tr>
                  <td
                    id={panelId}
                    colSpan={5}
                    className='border-b border-grey-lighter bg-grey-lightest/40 px-5 pb-4 pt-3'
                  >
                    {renderExpanded(agent)}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

AgentsRosterTable.propTypes = {
  items: PropTypes.array.isRequired,
  expandedAgentId: PropTypes.string,
  onToggleAgent: PropTypes.func.isRequired,
  renderExpanded: PropTypes.func.isRequired
};
